import React, { useEffect } from "react";
import { Form, Input, Button, Modal, message, Select, InputNumber } from "antd";
import { collection, doc, getDocs, orderBy, query, setDoc, updateDoc, where } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { Device } from "./web_parts/types";

/** 有効期限デフォルト（継続契約想定: 2099/1/1） */
const DEFAULT_EXPIRY_MS = new Date("2099-01-01T00:00:00.000Z").getTime();
const DEFAULT_EXPIRY_STR = "2099-01-01";

function toNonNegativeInt(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(0, Math.floor(value));
  }
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    if (Number.isFinite(n)) return Math.max(0, Math.floor(n));
  }
  return 0;
}

/** タイムスタンプ(ms) を YYYY-MM-DD に変換 */
function expiryMsToDateStr(ms: number | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return DEFAULT_EXPIRY_STR;
  return new Date(ms).toISOString().slice(0, 10);
}

/** YYYY-MM-DD をタイムスタンプ(ms) に変換 */
function dateStrToExpiryMs(s: string | undefined): number {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return DEFAULT_EXPIRY_MS;
  return new Date(s + "T00:00:00.000Z").getTime();
}

/**
 * Firestore devices ドキュメントの編集用フォーム値。
 * functions/src/index.ts の DeviceRecord に準拠（持っていないフィールドは含めない）。
 */
interface DeviceFormValues {
  deviceId: string;
  deviceName: string | null;
  manufacturer: string | null;
  model: string | null;
  osVersion: string | null;
  appVersion: string | null;
  fcmToken: string | null;
  userId: string | null;
  authProvider: string | null;
  userEmail: string | null;
  managerEmail: string | null;
  familyEmailsText: string | null;
  userDisplayName: string | null;
  userPhotoUrl: string | null;
  plan: "free" | "standard_plan";
  /** 有効期限（YYYY-MM-DD）。Input type="date" 用 */
  expiry: string | null;
  totalChatTimeMs: number | null;
  /** チャット警告時間（分）。0 は警告なし */
  chat_warn: number;
  /** チャット上限時間（分）。0 は上限なし */
  chat_limit: number;
}

interface ChatTimeMonthlyRow {
  month: string;
  totalChatMs: number;
}

const ENTITLEMENT_DENIED_HINT = "利用期限が切れているか";

type ProfileRefreshNotifyResult =
  | { ok: true }
  | { ok: false; entitlementDenied: boolean; detail: string };

async function notifyDeviceProfileRefresh(
  deviceId: string,
  fcmToken?: string | null
): Promise<ProfileRefreshNotifyResult> {
  const payload = {
    mode: "request",
    command: "refresh_device_profile",
    param1: deviceId,
    param2: String(Date.now()),
  };
  const body = fcmToken && fcmToken.trim().length > 0
    ? { fcmToken: fcmToken.trim(), title: "profile_refresh", body: "", data: payload }
    : { deviceId, title: "profile_refresh", body: "", data: payload };
  try {
    const response = await fetch("/api/sendFCM", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (response.ok) return { ok: true };
    const text = await response.text();
    return {
      ok: false,
      entitlementDenied: text.includes(ENTITLEMENT_DENIED_HINT),
      detail: text || "FCM通知に失敗しました",
    };
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    return { ok: false, entitlementDenied: false, detail };
  }
}

function parseFamilyEmailsText(input: string | null | undefined): string[] {
  if (!input) return [];
  return Array.from(
    new Set(
      input
        .split(/[\r\n,]+/)
        .map((email) => email.trim().toLowerCase())
        .filter((email) => email.length > 0)
    )
  );
}

function normalizeEmail(value: string | null | undefined): string | null {
  const email = (value ?? "").trim().toLowerCase();
  return email.length > 0 ? email : null;
}

function familyEmailsToText(emails: string[] | undefined): string {
  if (!emails || emails.length === 0) return "";
  return emails.join("\n");
}

interface DeviceFormProps {
  visible: boolean;
  onClose: (isUpdated?: boolean) => void;
  deviceData?: Device | null;
}

const Master_Device_RegForm: React.FC<DeviceFormProps> = ({ visible, onClose, deviceData }) => {
  const [form] = Form.useForm<DeviceFormValues>();
  const [monthlyRows, setMonthlyRows] = React.useState<ChatTimeMonthlyRow[]>([]);
  const [isMonthlyLoading, setIsMonthlyLoading] = React.useState(false);

  const totalFromRows = React.useMemo(
    () => monthlyRows.reduce((sum, row) => sum + (Number.isFinite(row.totalChatMs) ? row.totalChatMs : 0), 0),
    [monthlyRows]
  );
  const monthlyText = React.useMemo(() => {
    if (monthlyRows.length === 0) return "データなし";
    return monthlyRows
      .map((row) => `${row.month}: ${formatDurationMs(row.totalChatMs)}`)
      .join("\n");
  }, [monthlyRows]);

  async function fetchMonthlyChatTimes(deviceId: string): Promise<void> {
    setIsMonthlyLoading(true);
    try {
      const snap = await getDocs(
        query(
          collection(db, "chat_time"),
          where("deviceId", "==", deviceId),
          orderBy("month", "desc")
        )
      );
      const rows: ChatTimeMonthlyRow[] = [];
      snap.forEach((docSnap) => {
        const data = docSnap.data() as Record<string, unknown>;
        const month = typeof data.month === "string" ? data.month : "";
        const totalChatMs = typeof data.totalChatMs === "number" ? data.totalChatMs : 0;
        if (month) rows.push({ month, totalChatMs });
      });
      setMonthlyRows(rows);
    } catch (error) {
      console.error("月次会話時間の取得に失敗しました:", error);
      setMonthlyRows([]);
      message.warning("月次会話時間の取得に失敗しました。");
    } finally {
      setIsMonthlyLoading(false);
    }
  }

  // フォーム初期値：Firestore DeviceRecord のフィールドのみ
  useEffect(() => {
    if (!visible) return;
    if (deviceData) {
      form.setFieldsValue({
        deviceId: deviceData.id ?? deviceData.serialNumber,
        deviceName: deviceData.deviceName ?? null,
        manufacturer: deviceData.manufacturer ?? null,
        model: deviceData.model ?? null,
        osVersion: deviceData.osVersion ?? null,
        appVersion: deviceData.appVersion ?? null,
        fcmToken: deviceData.fcmToken ?? null,
        userId: deviceData.userId ?? null,
        authProvider: deviceData.authProvider ?? null,
        userEmail: deviceData.userEmail ?? null,
        managerEmail: deviceData.managerEmail ?? null,
        familyEmailsText: familyEmailsToText(deviceData.familyEmails),
        userDisplayName: deviceData.userDisplayName ?? deviceData.userInfo?.userName ?? null,
        userPhotoUrl: deviceData.userPhotoUrl ?? null,
        plan: deviceData.plan ?? "free",
        expiry: expiryMsToDateStr(deviceData.expiry),
        totalChatTimeMs: deviceData.totalChatTimeMs ?? null,
        chat_warn: toNonNegativeInt(deviceData.chat_warn),
        chat_limit: toNonNegativeInt(deviceData.chat_limit),
      });
      void fetchMonthlyChatTimes(deviceData.id ?? deviceData.serialNumber);
    } else {
      form.resetFields();
      form.setFieldsValue({
        plan: "free",
        expiry: DEFAULT_EXPIRY_STR,
        totalChatTimeMs: null,
        chat_warn: 0,
        chat_limit: 0,
      });
      setMonthlyRows([]);
    }
  }, [visible, deviceData, form]);

  const handleSubmit = async (values: DeviceFormValues) => {
    try {
      const now = Date.now();
      const docId = values.deviceId.trim();
      if (!docId) {
        message.error("デバイスIDは必須です。");
        return;
      }

      const firestoreData = {
        deviceId: docId,
        deviceName: values.deviceName?.trim() || null,
        manufacturer: values.manufacturer?.trim() || null,
        model: values.model?.trim() || null,
        osVersion: values.osVersion?.trim() || null,
        appVersion: values.appVersion?.trim() || null,
        fcmToken: values.fcmToken?.trim() || null,
        userId: values.userId?.trim() || null,
        authProvider: values.authProvider?.trim() || null,
        userEmail: normalizeEmail(values.userEmail),
        managerEmail: normalizeEmail(values.managerEmail),
        familyEmails: parseFamilyEmailsText(values.familyEmailsText),
        userDisplayName: values.userDisplayName?.trim() || null,
        userPhotoUrl: values.userPhotoUrl?.trim() || null,
        plan: values.plan ?? "free",
        expiry: dateStrToExpiryMs(values.expiry ?? undefined),
        chat_warn: toNonNegativeInt(values.chat_warn),
        chat_limit: toNonNegativeInt(values.chat_limit),
        updatedAt: now,
        isDeleted: false,
      };

      const docRef = doc(db, "devices", docId);

      if (deviceData) {
        await updateDoc(docRef, firestoreData);
        const notifyResult = await notifyDeviceProfileRefresh(
          docId,
          values.fcmToken ?? deviceData.fcmToken ?? null
        );
        if (notifyResult.ok) {
          message.success("デバイス情報を更新しました。");
        } else if (notifyResult.entitlementDenied) {
          message.warning(
            "データは更新されましたが、対象のデバイスは期限切れのため、FCMなどは送信されません。"
          );
        } else {
          console.warn("デバイス更新後のFCM通知に失敗:", notifyResult.detail);
          message.warning(
            "データは更新されましたが、端末への通知に失敗しました。"
          );
        }
      } else {
        await setDoc(docRef, {
          ...firestoreData,
          lastSyncTime: now,
          createdAt: now,
        });
        message.success("デバイスを登録しました。");
      }
      onClose(true);
    } catch (error) {
      console.error("デバイス情報の保存に失敗しました:", error);
      message.error(`保存に失敗しました: ${error}`);
    }
  };

  return (
    <Modal
      open={visible}
      title={deviceData ? "デバイス編集" : "デバイス新規登録"}
      onCancel={() => onClose(false)}
      footer={null}
      destroyOnClose
      className="device-reg-form-modal"
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="デバイスID（ドキュメントID）"
          name="deviceId"
          rules={[{ required: true, message: "デバイスIDは必須です。" }]}
        >
          <Input placeholder="例: 端末固有ID" disabled={!!deviceData} />
        </Form.Item>

        <Form.Item label="デバイス名" name="deviceName">
          <Input placeholder="例: CareTablet001" allowClear disabled />
        </Form.Item>

        <Form.Item label="メーカー" name="manufacturer">
          <Input placeholder="例: メーカー名" allowClear disabled />
        </Form.Item>

        <Form.Item label="モデル" name="model">
          <Input placeholder="例: モデル名" allowClear disabled />
        </Form.Item>

        <Form.Item label="OSバージョン" name="osVersion">
          <Input placeholder="例: 14" allowClear disabled />
        </Form.Item>

        <Form.Item label="アプリバージョン" name="appVersion">
          <Input placeholder="例: 1.0.0" allowClear disabled />
        </Form.Item>

        <Form.Item label="FCMトークン" name="fcmToken">
          <Input.TextArea rows={2} placeholder="デバイスから自動登録" disabled allowClear />
        </Form.Item>

        <Form.Item label="ユーザーID（Auth UID）" name="userId">
          <Input placeholder="Firebase Auth UID" disabled />
        </Form.Item>

        <Form.Item label="認証種別" name="authProvider">
          <Input placeholder="anonymous / google.com" disabled />
        </Form.Item>

        <Form.Item label="メール" name="userEmail">
          <Input placeholder="例: user@example.com" allowClear disabled />
        </Form.Item>

        <Form.Item label="管理ユーザーメール（managerEmail）" name="managerEmail">
          <Input placeholder="例: manager@example.com" allowClear />
        </Form.Item>

        <Form.Item
          label="親族メール（familyEmails）"
          name="familyEmailsText"
          extra="改行またはカンマ区切りで入力してください"
        >
          <Input.TextArea rows={3} placeholder={"例:\nfamily1@example.com\nfamily2@example.com"} allowClear />
        </Form.Item>

        <Form.Item label="表示名" name="userDisplayName">
          <Input placeholder="例: 山田 太郎" allowClear />
        </Form.Item>

        <Form.Item label="写真URL" name="userPhotoUrl">
          <Input placeholder="プロフィール画像URL" allowClear disabled />
        </Form.Item>

        <Form.Item label="プラン" name="plan" rules={[{ required: true }]}>
          <Select placeholder="プランを選択" options={[
            { value: "free", label: "free（お試し）" },
            { value: "standard_plan", label: "standard_plan（サブスク）" },
          ]} />
        </Form.Item>

        <Form.Item label="有効期限（サブスク）" name="expiry">
          <Input type="date" />
        </Form.Item>

        <Form.Item
          label="チャット警告時間（分）"
          name="chat_warn"
          rules={[
            { required: true, message: "警告時間を入力してください" },
            { type: "number", min: 0, message: "0 以上の整数で入力してください" },
          ]}
          extra="この時間以上になると警告ダイアログを表示します。0 で警告なし。"
        >
          <InputNumber min={0} step={1} precision={0} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item
          label="チャット上限時間（分）"
          name="chat_limit"
          rules={[
            { required: true, message: "上限時間を入力してください" },
            { type: "number", min: 0, message: "0 以上の整数で入力してください" },
          ]}
          extra="この時間以上になると新しい会話を開始できません。0 で上限なし。"
        >
          <InputNumber min={0} step={1} precision={0} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item label="トータル会話時間（全期間）">
          <Input value={formatDurationMs(deviceData?.totalChatTimeMs ?? totalFromRows)} disabled />
        </Form.Item>

        <Form.Item label="月次会話時間（全月・参照専用）">
          <Input.TextArea
            value={isMonthlyLoading ? "読み込み中..." : monthlyText}
            autoSize={false}
            style={{ height: 240, overflowY: "auto" }}
            disabled
          />
        </Form.Item>

        <Form.Item>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => onClose(false)}>キャンセル</Button>
            <Button type="primary" htmlType="submit">
              {deviceData ? "更新" : "登録"}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default Master_Device_RegForm;

function formatDurationMs(ms?: number): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return "—";
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  return `${hours}時間 ${minutes}分`;
}
